import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import VerifiedBadge from '../components/VerifiedBadge';
import Toast from '../components/Toast';
import api from '../api';

/**
 * /verify — creator-facing verification workflow.
 * MVP: social handle + bio-code method (admin manual review).
 * States surfaced:
 *   - already verified   → success state with badge
 *   - no application     → show submit form (platform picker + handle input)
 *   - pending            → show bio-code + "waiting" state + cancel button
 *   - rejected           → show reason + "apply again" (routes back to form)
 */
export default function Verify() {
  const [data, setData] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('x');
  const [socialHandle, setSocialHandle] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [applyAgain, setApplyAgain] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/verify');
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    // Handle OAuth callback query params (?x=ok or ?x=err&detail=...)
    const xResult = searchParams.get('x');
    if (xResult === 'ok') {
      setToast({ msg: '✓ Verified with X!' });
      searchParams.delete('x');
      setSearchParams(searchParams, { replace: true });
    } else if (xResult === 'err') {
      const detail = searchParams.get('detail') || 'X verification failed';
      setToast({ msg: `X verification failed: ${detail}` });
      searchParams.delete('x'); searchParams.delete('detail');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    api.get('/verify/platforms')
      .then(({ data }) => setPlatforms(data.platforms || []))
      .catch(() => setPlatforms([
        { key: 'instagram', label: 'Instagram' },
        { key: 'x', label: 'X (Twitter)' },
        { key: 'tiktok', label: 'TikTok' },
        { key: 'youtube', label: 'YouTube' },
      ]));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    const handle = socialHandle.trim().replace(/^@/, '');
    if (handle.length < 2) {
      setToast({ msg: 'Enter your social handle.' });
      return;
    }

    // Phase 7: X uses OAuth 2.0 automatic verification. All other platforms
    // still use the legacy bio-code workflow.
    if (selectedPlatform === 'x') {
      setBusy(true);
      try {
        const { data } = await api.post('/verify/x/start', { social_handle: handle });
        // Redirect the browser to X's OAuth authorize URL.
        window.location.href = data.authorize_url;
        return;
      } catch (e) {
        setToast({ msg: e?.response?.data?.detail || 'Could not start X verification.' });
        setBusy(false);
        return;
      }
    }

    setBusy(true);
    try {
      await api.post('/verify/submit', { platform: selectedPlatform, social_handle: handle });
      setSocialHandle('');
      setApplyAgain(false);
      setToast({ msg: 'Application submitted — check back once we review.' });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Submit failed' });
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (busy) return;
    if (!window.confirm('Cancel this verification application?')) return;
    setBusy(true);
    try {
      await api.post('/verify/cancel');
      setToast({ msg: 'Application cancelled.' });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Cancel failed' });
    } finally { setBusy(false); }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  if (loading) {
    return <AppShell><div className="page-wrap"><div className="loading-shell">Loading…</div></div></AppShell>;
  }
  if (err) {
    return <AppShell><div className="page-wrap"><div className="admin-error">{err}</div></div></AppShell>;
  }

  const already = !!data?.verified;
  const app = data?.application;
  // Verification no longer requires a Clan — creators can verify BEFORE
  // launch so they unlock the "Schedule launch" flow (which is verified-gated).
  const canApply = data?.eligibility?.profile_complete;

  return (
    <AppShell>
      <div className="page-wrap" data-testid="verify-page">
        <header className="verify-header">
          <h1 className="verify-title">
            ✓ Get verified
          </h1>
          <p className="verify-lede">
            The verified badge is a small blue check next to your name. It tells your Clan you're
            the real deal. It's free — we just need to confirm you own the social account you claim.
          </p>
        </header>

        {/* ---------- STATE: Already verified ---------- */}
        {already && (
          <div className="verify-card verify-card-success" data-testid="verify-success">
            <div className="verify-success-emoji">🎉</div>
            <h2 className="verify-card-title">
              You're verified <VerifiedBadge size="md" />
            </h2>
            <p className="verify-card-body">
              {data?.eligibility?.has_clan
                ? "The badge is live on your profile, Home feed cards, and every Pulse row."
                : "The badge is ready — it'll show on your profile the moment you mint your Clan. Head back to your checklist to keep the momentum going."}
            </p>
            {data?.eligibility?.has_clan && data?.eligibility?.clan_handle ? (
              <Link
                to={`/creator/${data.eligibility.clan_handle}`}
                className="verify-primary-btn"
                data-testid="verify-view-profile"
              >
                View my profile
              </Link>
            ) : (
              <Link
                to="/create-clan"
                className="verify-primary-btn"
                data-testid="verify-back-to-create-clan"
              >
                Back to Create Clan →
              </Link>
            )}
          </div>
        )}

        {/* ---------- STATE: Profile incomplete ---------- */}
        {!already && !canApply && (
          <div className="verify-card verify-card-warn" data-testid="verify-blocked">
            <h2 className="verify-card-title">One thing first…</h2>
            <p className="verify-card-body">
              Complete your profile before you apply for verification.
            </p>
            <button
              type="button"
              className="verify-primary-btn"
              onClick={() => navigate('/settings')}
              data-testid="verify-fix-eligibility"
            >
              Finish profile →
            </button>
          </div>
        )}

        {/* ---------- STATE: Pending review ---------- */}
        {!already && app && app.status === 'pending' && !applyAgain && (
          <div className="verify-card verify-card-pending" data-testid="verify-pending">
            <div className="verify-status-chip verify-status-pending">
              <span className="dot" /> Pending review
            </div>
            <h2 className="verify-card-title">Waiting for the Clanster team</h2>
            <p className="verify-card-body">
              Reviews typically take 2–3 business days. You'll get a notification the moment a decision is made.
            </p>

            <div className="verify-app-summary">
              <div className="verify-app-row">
                <span className="verify-app-label">Platform</span>
                <span className="verify-app-value">{app.platform_label}</span>
              </div>
              <div className="verify-app-row">
                <span className="verify-app-label">Handle</span>
                <a href={app.social_url} target="_blank" rel="noreferrer" className="verify-app-value verify-link" data-testid="verify-social-url">
                  @{app.social_handle} ↗
                </a>
              </div>
            </div>

            <div className="verify-code-box" data-testid="verify-bio-code-box">
              <div className="verify-code-label">Paste this into your bio</div>
              <div className="verify-code-value" data-testid="verify-bio-code">{app.bio_code}</div>
              <button
                type="button"
                className={`verify-code-copy${copied ? ' copied' : ''}`}
                onClick={() => copyCode(app.bio_code)}
                data-testid="verify-copy-code"
              >
                {copied ? '✓ Copied' : 'Copy code'}
              </button>
            </div>
            <p className="verify-code-hint">
              Keep this code in your public bio until our team confirms it. We'll never DM you asking
              for passwords — the code is all we need.
            </p>

            <button
              type="button"
              className="verify-secondary-btn"
              onClick={cancel}
              disabled={busy}
              data-testid="verify-cancel"
            >
              Cancel application
            </button>
          </div>
        )}

        {/* ---------- STATE: Rejected — apply again ---------- */}
        {!already && app && app.status === 'rejected' && !applyAgain && (
          <div className="verify-card verify-card-rejected" data-testid="verify-rejected">
            <div className="verify-status-chip verify-status-rejected">
              <span className="dot" /> Not approved
            </div>
            <h2 className="verify-card-title">Your last application wasn't approved</h2>
            {app.rejection_reason && (
              <p className="verify-card-body verify-reason">
                <b>Reason:</b> {app.rejection_reason}
              </p>
            )}
            <p className="verify-card-body">
              No hard feelings — you can update your details and apply again right away.
            </p>
            <button
              type="button"
              className="verify-primary-btn"
              onClick={() => setApplyAgain(true)}
              data-testid="verify-apply-again"
            >
              Apply again →
            </button>
          </div>
        )}

        {/* ---------- STATE: Submit form ---------- */}
        {!already && canApply && (!app || app.status === 'cancelled' || applyAgain) && (
          <form className="verify-card" onSubmit={submit} data-testid="verify-form">
            <h2 className="verify-card-title">Apply for verification</h2>
            <p className="verify-card-body">
              Pick the social account you'd like to verify from. We'll give you a short code to paste in
              your bio; one of our team will confirm it and flip the badge on.
            </p>

            <div className="verify-label">Where can we verify you?</div>
            <div className="verify-platform-grid">
              {platforms.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`verify-platform-btn${selectedPlatform === p.key ? ' active' : ''}`}
                  onClick={() => setSelectedPlatform(p.key)}
                  data-testid={`verify-platform-${p.key}`}
                >
                  {p.label}
                  {p.key === 'x' && <span className="verify-plat-tag">Instant ⚡</span>}
                </button>
              ))}
            </div>
            {selectedPlatform !== 'x' && (
              <p className="verify-plat-note" data-testid="verify-non-x-note">
                Instagram, TikTok, and YouTube verification coming later — for now, verify instantly with X (Twitter) using OAuth.
              </p>
            )}

            <label className="verify-label" htmlFor="verify-handle-input">Your @handle</label>
            <div className="verify-input-wrap">
              <span className="verify-input-prefix">@</span>
              <input
                id="verify-handle-input"
                type="text"
                className="verify-input"
                placeholder={selectedPlatform === 'x' ? 'e.g. mayacreates' : 'e.g. mayacreates'}
                value={socialHandle}
                onChange={(e) => setSocialHandle(e.target.value)}
                autoComplete="off"
                spellCheck="false"
                maxLength={40}
                data-testid="verify-handle-input"
              />
            </div>

            <button
              type="submit"
              className="verify-primary-btn"
              disabled={busy || !socialHandle.trim()}
              data-testid="verify-submit"
            >
              {busy
                ? 'Redirecting…'
                : selectedPlatform === 'x'
                  ? 'Verify with X →'
                  : 'Get my bio code →'}
            </button>

            {applyAgain && (
              <button
                type="button"
                className="verify-secondary-btn"
                onClick={() => setApplyAgain(false)}
                data-testid="verify-cancel-reapply"
              >
                Never mind
              </button>
            )}
          </form>
        )}

        <div className="verify-fine-print">
          Verification is a trust check, not a paid feature. It never expires — you only need to apply once.
        </div>
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AppShell>
  );
}
